import { nextTick } from '@patrianna/shared-utils'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { useRef } from 'react'

import { Accordion } from 'components/Accordion'
import type { ItemProps } from 'pageContainers/TransactionsPage/components/FiltersBlock/components/DateFilter/types'
import {
  getValidatedDateRange,
  formatRange,
  getDateInPast,
} from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import type { DateRangeProps } from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import content from 'prismic-translates/transactions-history-page.json'
import { DatePicker } from 'src/components/DatePicker'

import classes from './styles.module.scss'

const t = getTranslationFromJson([content])

type Props = {
  handleChange: (range: DateRangeProps) => void
  selectedValue: ItemProps
  setSelectedValue: (option: ItemProps) => void
  dateRange: DateRangeProps
  setDateRange: (rage: DateRangeProps) => void
  defaultOption: ItemProps
}

export const CUSTOM_RANGE = 'CUSTOM_RANGE'
export const DEFAULT_DATE_RANGE: DateRangeProps = { startDate: null, endDate: null }
const MAX_90_DAYS_RANGE = getDateInPast(90)

const basePickerProps = {
  maxDate: new Date(),
  minDate: MAX_90_DAYS_RANGE,
  disableFuture: true,
  size: 'small',
  inputVariant: 'outlined',
  format: 'MMM d, yyyy',
  views: ['date'],
  openView: 'date',
  okLabel: 'OK',
  cancelLabel: 'Cancel',
}

export default function CustomRange({
  handleChange,
  setSelectedValue,
  selectedValue,
  dateRange,
  setDateRange,
  defaultOption,
}: Props) {
  const datePickerRef = useRef<HTMLDivElement | null>(null)

  const { startDate, endDate } = dateRange

  const handleClearFilter = (): void => {
    setSelectedValue(defaultOption)
    setDateRange(DEFAULT_DATE_RANGE)
    handleChange(null)
  }

  const handleChangeDatePicker = (key: 'startDate' | 'endDate') => (date: Date) => {
    nextTick(() => {
      /*
       * [FYI]: set HH:mm need for correct showing UTC format
       * */
      const currentDate = new Date()

      date.setHours(currentDate.getHours())
      date.setMinutes(currentDate.getMinutes())

      const range: DateRangeProps = { ...dateRange, [key]: formatRange(date, 'MMM d, yyyy HH:mm') }

      setDateRange(range)

      if (selectedValue?.value !== CUSTOM_RANGE) {
        setSelectedValue({ value: CUSTOM_RANGE, label: '', data: '' })
      }

      handleChange(getValidatedDateRange(range))
    })
  }

  return (
    <div className={classes.customRange}>
      <Accordion
        classNameIsOpened='accordionIsOpened'
        classNameTitleBlock={classes.accordionTitleBlock}
        title={t('custom_date_range')}
        content={
          <div className={classes.contentWrapper}>
            <div className={classes.rage} ref={datePickerRef}>
              <DatePicker
                {...basePickerProps}
                value={startDate}
                onChange={handleChangeDatePicker('startDate')}
                maxDate={endDate ? new Date(endDate) : basePickerProps?.maxDate}
                label={t('from_label')}
                DialogProps={{ container: datePickerRef.current, disablePortal: true }}
                dataTestId='datepicker-from-select'
              />

              <DatePicker
                {...basePickerProps}
                value={endDate}
                onChange={handleChangeDatePicker('endDate')}
                minDate={startDate ? new Date(startDate) : basePickerProps?.minDate}
                label={t('to_label')}
                DialogProps={{ container: datePickerRef.current, disablePortal: true }}
                dataTestId='datepicker-to-select'
              />
            </div>

            <button
              type='button'
              className={classes.clearButton}
              onClick={handleClearFilter}
              disabled={!dateRange?.startDate && !dateRange?.endDate}
              data-testid='custom-range-clear-button'
            >
              {t('clear_date_button')}
            </button>
          </div>
        }
      />
    </div>
  )
}
